import React, { useState, useEffect, useCallback } from 'react';
import {
  MapPin,
  ArrowDown,
  ArrowUp,
  GripVertical,
  Plus,
  Trash2,
  Save,
  CheckCircle2,
  Clock,
  Truck,
  AlertTriangle,
  Navigation,
  Sparkles,
  Layers,
  ArrowRight,
  Filter,
  Users,
  Calendar
} from 'lucide-react';
import { supabase } from '../lib/supabase';
import { RoteiroMotorista, RoteiroItem, PrioridadeRoteiro, TipoParadaRoteiro } from '../types/roteiro';
import { roteiroService } from '../services/roteiroService';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  motoristaId?: number;
  motoristaNome?: string;
  initialDate?: string;
  onRoteiroSalvo?: (roteiro: RoteiroMotorista) => void;
}

export const CriadorRoteiroModal: React.FC<Props> = ({
  isOpen,
  onClose,
  motoristaId,
  motoristaNome,
  initialDate,
  onRoteiroSalvo,
}) => {
  const [motoristas, setMotoristas] = useState<any[]>([]);
  const [selectedMotoristaId, setSelectedMotoristaId] = useState<number | undefined>(motoristaId);
  const [dataRoteiro, setDataRoteiro] = useState<string>(initialDate || new Date().toISOString().split('T')[0]);
  const [nomeRoteiro, setNomeRoteiro] = useState<string>('Roteiro Otimizado do Dia');
  const [observacoes, setObservacoes] = useState<string>('');
  const [filtrarApenasComOSNaData, setFiltrarApenasComOSNaData] = useState<boolean>(true);
  const [filtrarOSPorData, setFiltrarOSPorData] = useState<boolean>(true);

  // Ordens de Serviço disponíveis para vincular
  const [osDisponiveis, setOsDisponiveis] = useState<any[]>([]);
  const [carregandoOS, setCarregandoOS] = useState<boolean>(false);
  const [carregandoMotoristas, setCarregandoMotoristas] = useState<boolean>(false);

  // Lista ordenada de paradas do roteiro
  const [paradas, setParadas] = useState<RoteiroItem[]>([]);
  const [salvando, setSalvando] = useState<boolean>(false);
  const [sucessoMsg, setSucessoMsg] = useState<string | null>(null);

  // Carrega lista de motoristas ativos, filtrando pela data da execução se selecionado
  const loadMotoristas = useCallback(async () => {
    if (!isOpen) return;
    setCarregandoMotoristas(true);
    try {
      if (filtrarApenasComOSNaData && dataRoteiro) {
        // 1. Buscar OSs ativas na data selecionada (excluindo canceladas id 3 e deletadas)
        const { data: osNaData } = await supabase
          .from('ordem_de_servico')
          .select('agregado')
          .eq('data_atendimento', dataRoteiro)
          .is('deleted_at', null)
          .neq('status_id', 3)
          .not('agregado', 'is', null);

        // Agrupar contagem de OSs por motorista nesta data
        const contagemPorMotorista: Record<number, number> = {};
        (osNaData || []).forEach((os: any) => {
          const mId = Number(os.agregado);
          if (!isNaN(mId) && mId > 0) {
            contagemPorMotorista[mId] = (contagemPorMotorista[mId] || 0) + 1;
          }
        });

        const motoristaIdsComOS = Object.keys(contagemPorMotorista).map(Number);

        if (motoristaIdsComOS.length > 0) {
          // 2. Buscar apenas motoristas ATIVOS e NÃO DELETADOS
          const { data: pessoasAtivas } = await supabase
            .from('pessoa')
            .select('id, nome, documento, ativo, deleted_at')
            .in('id', motoristaIdsComOS)
            .is('deleted_at', null)
            .or('ativo.is.null,ativo.eq.true')
            .order('nome');

          if (pessoasAtivas && pessoasAtivas.length > 0) {
            const listWithCount = pessoasAtivas.map(p => ({
              ...p,
              totalOSNaData: contagemPorMotorista[p.id] || 0
            }));

            setMotoristas(listWithCount);

            // Ajusta o motorista selecionado para um que realmente tenha OS na data
            setSelectedMotoristaId(prev => {
              if (prev && listWithCount.some(m => m.id === prev)) {
                return prev;
              }
              if (motoristaId && listWithCount.some(m => m.id === motoristaId)) {
                return motoristaId;
              }
              return listWithCount[0]?.id;
            });
            return;
          }
        }

        // Se não encontrou nenhum motorista com OS na data
        setMotoristas([]);
        setSelectedMotoristaId(undefined);
      } else {
        // Modo "Todos os Motoristas Ativos": Buscar cadastros de motoristas/agregados ativos
        const { data: todasPessoasAtivas } = await supabase
          .from('pessoa')
          .select('id, nome, documento, ativo, deleted_at, categoria_cliente_id')
          .is('deleted_at', null)
          .or('ativo.is.null,ativo.eq.true')
          .in('categoria_cliente_id', [3, 4]) // 3: Agregado, 4: Colaborador
          .order('nome');

        if (todasPessoasAtivas && todasPessoasAtivas.length > 0) {
          setMotoristas(todasPessoasAtivas);
          if (!selectedMotoristaId) {
            setSelectedMotoristaId(motoristaId || todasPessoasAtivas[0].id);
          }
        } else {
          // Fallback para pessoas gerais ativas se categorias não estiverem populadas
          const { data: fallbackAtivos } = await supabase
            .from('pessoa')
            .select('id, nome, documento, ativo, deleted_at')
            .is('deleted_at', null)
            .or('ativo.is.null,ativo.eq.true')
            .order('nome')
            .limit(50);
          setMotoristas(fallbackAtivos || []);
        }
      }
    } catch (err) {
      console.error('Erro ao carregar motoristas para roteirizador:', err);
    } finally {
      setCarregandoMotoristas(false);
    }
  }, [isOpen, dataRoteiro, filtrarApenasComOSNaData, motoristaId]);

  useEffect(() => {
    loadMotoristas();
  }, [loadMotoristas]);

  // Atualiza motorista selecionado quando mudar a prop inicial
  useEffect(() => {
    if (motoristaId) {
      setSelectedMotoristaId(motoristaId);
    }
  }, [motoristaId]);

  // Carrega OS do motorista selecionado (filtrando pela data da execução ou todas abertas)
  useEffect(() => {
    if (!isOpen || !selectedMotoristaId) {
      setOsDisponiveis([]);
      return;
    }

    async function loadOS() {
      setCarregandoOS(true);
      try {
        let q = supabase
          .from('ordem_de_servico')
          .select('id, pedido_id, data_atendimento, retirada, entrega, descricao, repasse, status_id, veiculos_id')
          .eq('agregado', selectedMotoristaId)
          .is('deleted_at', null)
          .neq('status_id', 3) // Não cancelada
          .order('id', { ascending: false });

        if (filtrarOSPorData && dataRoteiro) {
          q = q.eq('data_atendimento', dataRoteiro);
        }

        const { data: osData, error } = await q.limit(50);
        if (!error && osData) {
          setOsDisponiveis(osData);
        }
      } catch (err) {
        console.error('Erro ao carregar OS para roteirizador:', err);
      } finally {
        setCarregandoOS(false);
      }
    }

    loadOS();
  }, [isOpen, selectedMotoristaId, dataRoteiro, filtrarOSPorData]);

  // Importa todas as coletas e entregas das OS selecionadas
  const handleImportarTodasOS = () => {
    if (osDisponiveis.length === 0) return;
    const novasParadas = roteiroService.converterOSToParadas(osDisponiveis);
    setParadas(novasParadas);
  };

  // Adiciona uma parada avulsa ou de uma OS
  const handleAdicionarParadaDeOS = (os: any, tipo: TipoParadaRoteiro) => {
    const endereco = tipo === 'coleta' ? (os.retirada || 'Endereço de Coleta') : (os.entrega || 'Endereço de Entrega');
    const nova: RoteiroItem = {
      ordem_de_servico_id: os.id,
      tipo_parada: tipo,
      ordem_sequencia: paradas.length + 1,
      prioridade: 'normal',
      endereco: endereco,
      status_parada: 'pendente',
      descricao_carga: os.descricao || `OS #${os.id}`,
      valor_repasse: Number(os.repasse) || 0
    };
    setParadas([...paradas, nova]);
  };

  // Move uma parada para cima na sequência
  const handleMoverCima = (index: number) => {
    if (index === 0) return;
    const novaLista = [...paradas];
    const temp = novaLista[index - 1];
    novaLista[index - 1] = novaLista[index];
    novaLista[index] = temp;
    // Reindexar sequência
    setParadas(novaLista.map((p, i) => ({ ...p, ordem_sequencia: i + 1 })));
  };

  // Move uma parada para baixo na sequência
  const handleMoverBaixo = (index: number) => {
    if (index === paradas.length - 1) return;
    const novaLista = [...paradas];
    const temp = novaLista[index + 1];
    novaLista[index + 1] = novaLista[index];
    novaLista[index] = temp;
    // Reindexar sequência
    setParadas(novaLista.map((p, i) => ({ ...p, ordem_sequencia: i + 1 })));
  };

  // Remove parada da rota
  const handleRemoverParada = (index: number) => {
    const novaLista = paradas.filter((_, i) => i !== index);
    setParadas(novaLista.map((p, i) => ({ ...p, ordem_sequencia: i + 1 })));
  };

  // Altera a prioridade de uma parada
  const handleMudarPrioridade = (index: number, prioridade: PrioridadeRoteiro) => {
    const novaLista = [...paradas];
    novaLista[index].prioridade = prioridade;
    setParadas(novaLista);
  };

  // Otimização Inteligente: Colocar todas as coletas primeiro e depois as entregas
  const handleOtimizarColetasPrimeiro = () => {
    const coletas = paradas.filter(p => p.tipo_parada === 'coleta');
    const entregas = paradas.filter(p => p.tipo_parada !== 'coleta');
    const reordenadas = [...coletas, ...entregas].map((p, i) => ({
      ...p,
      ordem_sequencia: i + 1
    }));
    setParadas(reordenadas);
  };

  // Salvar roteiro programado
  const handleSalvar = async () => {
    if (!selectedMotoristaId) {
      alert('Por favor, selecione um motorista para vincular este roteiro.');
      return;
    }
    if (paradas.length === 0) {
      alert('Adicione pelo menos uma parada na rota do motorista.');
      return;
    }

    setSalvando(true);
    try {
      const motoristaObj = motoristas.find(m => m.id === selectedMotoristaId);
      const roteiroSalvo = await roteiroService.salvarRoteiro({
        motorista_id: selectedMotoristaId,
        motorista_nome: motoristaObj?.nome || motoristaNome || 'Motorista',
        data_roteiro: dataRoteiro,
        nome_roteiro: nomeRoteiro,
        observacoes,
        status: 'planejado',
        itens: paradas
      });

      setSucessoMsg('Roteiro programado e ordenado com sucesso para o motorista!');
      if (onRoteiroSalvo) onRoteiroSalvo(roteiroSalvo);

      setTimeout(() => {
        setSucessoMsg(null);
        onClose();
      }, 1500);
    } catch (err) {
      console.error('Erro ao salvar roteiro:', err);
      alert('Ocorreu um erro ao salvar o roteiro.');
    } finally {
      setSalvando(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-xs flex items-center justify-center p-3 sm:p-6 overflow-y-auto">
      <div className="bg-slate-900 border border-slate-700 rounded-3xl w-full max-w-5xl max-h-[92vh] flex flex-col shadow-2xl overflow-hidden text-left">
        
        {/* Header do Roteirizador */}
        <div className="p-4 sm:p-6 border-b border-slate-800 flex items-center justify-between bg-slate-950/60">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-2xl bg-red-600/20 text-red-500 flex items-center justify-center border border-red-500/30">
              <Navigation className="w-5 h-5" />
            </div>
            <div>
              <h2 className="text-base sm:text-lg font-black text-white flex items-center gap-2">
                <span>Roteirizador e Sequenciamento de OS</span>
                <span className="text-[10px] uppercase font-bold tracking-wider px-2 py-0.5 rounded bg-red-950 text-red-400 border border-red-800">
                  Programação de Rota
                </span>
              </h2>
              <p className="text-xs text-slate-400">
                Defina a ordem exata de coletas vizinhas e entregas para o motorista seguir no aplicativo.
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="p-2 rounded-xl text-slate-400 hover:text-white hover:bg-slate-800 transition"
          >
            ✕
          </button>
        </div>

        {/* Mensagem de Sucesso */}
        {sucessoMsg && (
          <div className="mx-6 mt-4 p-3 rounded-2xl bg-emerald-950/80 border border-emerald-800 text-emerald-300 text-xs font-bold flex items-center gap-2 animate-fadeIn">
            <CheckCircle2 className="w-4 h-4" />
            {sucessoMsg}
          </div>
        )}

        {/* Corpo Principal: Configurações + Seleção de OS + Sequência */}
        <div className="p-4 sm:p-6 overflow-y-auto space-y-5 flex-1">
          
          {/* Barra de Configurações Básicas */}
          <div className="p-4 rounded-2xl bg-slate-950/70 border border-slate-800 space-y-3">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {/* Data da Execução */}
              <div>
                <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1 flex items-center gap-1">
                  <Calendar className="w-3.5 h-3.5 text-red-400" />
                  Data da Execução
                </label>
                <input
                  type="date"
                  value={dataRoteiro}
                  onChange={(e) => setDataRoteiro(e.target.value)}
                  className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-xs text-white focus:border-red-500 outline-none font-medium"
                />
              </div>

              {/* Motorista Atribuído */}
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider flex items-center gap-1">
                    <Truck className="w-3.5 h-3.5 text-red-400" />
                    Motorista Atribuído
                  </label>
                  <span className="text-[10px] text-slate-500 font-medium">
                    {carregandoMotoristas ? 'Carregando...' : `${motoristas.length} ativo(s)`}
                  </span>
                </div>
                <select
                  value={selectedMotoristaId || ''}
                  onChange={(e) => setSelectedMotoristaId(e.target.value ? Number(e.target.value) : undefined)}
                  className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-xs text-white focus:border-red-500 outline-none font-medium"
                >
                  {motoristas.length === 0 ? (
                    <option value="">
                      {carregandoMotoristas ? 'Buscando motoristas...' : 'Nenhum motorista com OS nesta data'}
                    </option>
                  ) : (
                    <>
                      <option value="">-- Selecione o Motorista --</option>
                      {motoristas.map((m) => (
                        <option key={m.id} value={m.id}>
                          🚚 {m.nome} {m.totalOSNaData !== undefined ? `(${m.totalOSNaData} OS${m.totalOSNaData > 1 ? 's' : ''} no dia)` : ''}
                        </option>
                      ))}
                    </>
                  )}
                </select>
              </div>

              {/* Identificação do Roteiro */}
              <div>
                <label className="block text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                  Identificação do Roteiro
                </label>
                <input
                  type="text"
                  value={nomeRoteiro}
                  onChange={(e) => setNomeRoteiro(e.target.value)}
                  placeholder="Ex: Roteiro SP - Coletas Zona Leste"
                  className="w-full px-3 py-2 rounded-xl bg-slate-900 border border-slate-700 text-xs text-white focus:border-red-500 outline-none"
                />
              </div>
            </div>

            {/* Filtros Rápidos de Motoristas e OS */}
            <div className="pt-2 border-t border-slate-800/80 flex flex-wrap items-center justify-between gap-2 text-xs">
              <div className="flex items-center gap-3 flex-wrap">
                <label className="flex items-center gap-1.5 cursor-pointer text-slate-300 hover:text-white select-none">
                  <input
                    type="checkbox"
                    checked={filtrarApenasComOSNaData}
                    onChange={(e) => setFiltrarApenasComOSNaData(e.target.checked)}
                    className="rounded bg-slate-900 border-slate-700 text-red-600 focus:ring-0 w-3.5 h-3.5"
                  />
                  <span>Mostrar apenas motoristas com OS na data ({dataRoteiro ? new Date(dataRoteiro + 'T12:00:00').toLocaleDateString('pt-BR') : ''})</span>
                </label>

                <label className="flex items-center gap-1.5 cursor-pointer text-slate-300 hover:text-white select-none">
                  <input
                    type="checkbox"
                    checked={filtrarOSPorData}
                    onChange={(e) => setFiltrarOSPorData(e.target.checked)}
                    className="rounded bg-slate-900 border-slate-700 text-red-600 focus:ring-0 w-3.5 h-3.5"
                  />
                  <span>Filtrar OSs apenas desta data</span>
                </label>
              </div>

              <div className="flex items-center gap-1.5 text-[11px] text-emerald-400 font-bold bg-emerald-950/40 px-2.5 py-1 rounded-lg border border-emerald-900/60">
                <CheckCircle2 className="w-3.5 h-3.5" />
                <span>Apenas motoristas ativos e sem exclusão</span>
              </div>
            </div>
          </div>

          {/* Grid com Duas Colunas: OS Disponíveis vs Rota Ordenada */}
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            
            {/* Coluna Esquerda: OS Disponíveis para Roteirizar */}
            <div className="lg:col-span-5 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-black text-slate-200 uppercase tracking-wider flex items-center gap-1.5">
                  <Layers className="w-3.5 h-3.5 text-red-400" />
                  Ordens de Serviço ({osDisponiveis.length})
                </h3>
                {osDisponiveis.length > 0 && (
                  <button
                    onClick={handleImportarTodasOS}
                    className="text-[11px] font-bold text-red-400 hover:text-red-300 underline"
                  >
                    Adicionar Todas
                  </button>
                )}
              </div>

              <div className="space-y-2 max-h-[380px] overflow-y-auto pr-1">
                {carregandoOS ? (
                  <div className="p-8 text-center text-xs text-slate-500">Carregando OS do motorista...</div>
                ) : !selectedMotoristaId ? (
                  <div className="p-6 text-center rounded-2xl bg-slate-950/40 border border-slate-800 text-xs text-slate-500">
                    Selecione um motorista acima para carregar suas OSs.
                  </div>
                ) : osDisponiveis.length === 0 ? (
                  <div className="p-6 text-center rounded-2xl bg-slate-950/40 border border-slate-800 space-y-2">
                    <p className="text-xs text-slate-400 font-medium">Nenhuma OS encontrada para este motorista {filtrarOSPorData ? 'nesta data' : ''}.</p>
                    {filtrarOSPorData && (
                      <button
                        onClick={() => setFiltrarOSPorData(false)}
                        className="text-[11px] text-red-400 hover:text-red-300 font-bold underline"
                      >
                        Ver todas as OSs abertas do motorista (qualquer data)
                      </button>
                    )}
                  </div>
                ) : (
                  osDisponiveis.map((os) => (
                    <div
                      key={os.id}
                      className="p-3 rounded-2xl bg-slate-950/60 border border-slate-800 space-y-2 text-xs hover:border-slate-700 transition"
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-bold text-white">OS #{os.id}</span>
                        <span className="text-[10px] text-slate-400 font-mono">
                          {os.data_atendimento ? new Date(os.data_atendimento + 'T12:00:00').toLocaleDateString('pt-BR') : ''}
                        </span>
                      </div>

                      {/* Botões para adicionar Coleta ou Entrega */}
                      <div className="space-y-1 text-[11px]">
                        {os.retirada && (
                          <div className="flex items-center justify-between gap-2 p-1.5 rounded-lg bg-slate-900 border border-slate-800">
                            <span className="text-amber-300 truncate" title={os.retirada}>📦 Coleta: {os.retirada}</span>
                            <button
                              onClick={() => handleAdicionarParadaDeOS(os, 'coleta')}
                              className="px-2 py-0.5 rounded bg-amber-600/30 hover:bg-amber-600 text-amber-200 text-[10px] font-bold shrink-0 transition"
                            >
                              + Coleta
                            </button>
                          </div>
                        )}

                        {os.entrega && (
                          <div className="flex items-center justify-between gap-2 p-1.5 rounded-lg bg-slate-900 border border-slate-800">
                            <span className="text-emerald-300 truncate" title={os.entrega}>🏁 Entrega: {os.entrega}</span>
                            <button
                              onClick={() => handleAdicionarParadaDeOS(os, 'entrega')}
                              className="px-2 py-0.5 rounded bg-emerald-600/30 hover:bg-emerald-600 text-emerald-200 text-[10px] font-bold shrink-0 transition"
                            >
                              + Entrega
                            </button>
                          </div>
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>

            {/* Coluna Direita: Sequência Programada da Rota */}
            <div className="lg:col-span-7 space-y-3">
              <div className="flex items-center justify-between">
                <h3 className="text-xs font-black text-slate-200 uppercase tracking-wider flex items-center gap-1.5">
                  <Navigation className="w-3.5 h-3.5 text-emerald-400" />
                  Ordem de Execução ({paradas.length} Paradas)
                </h3>

                {paradas.length > 1 && (
                  <button
                    onClick={handleOtimizarColetasPrimeiro}
                    className="px-2.5 py-1 rounded-xl bg-purple-950/80 hover:bg-purple-900 border border-purple-800 text-purple-300 text-[11px] font-bold flex items-center gap-1 transition"
                  >
                    <Sparkles className="w-3 h-3" />
                    Agrupar Coletas Primeiro
                  </button>
                )}
              </div>

              {paradas.length === 0 ? (
                <div className="p-12 text-center rounded-3xl border-2 border-dashed border-slate-800 bg-slate-950/30 space-y-2">
                  <MapPin className="w-8 h-8 mx-auto text-slate-600" />
                  <p className="text-xs font-bold text-slate-400">Nenhuma parada adicionada no roteiro ainda.</p>
                  <p className="text-[11px] text-slate-500">
                    Clique em "+ Coleta" ou "+ Entrega" nas Ordens de Serviço ao lado para montar a rota.
                  </p>
                </div>
              ) : (
                <div className="space-y-2.5 max-h-[400px] overflow-y-auto pr-1">
                  {paradas.map((parada, idx) => (
                    <div
                      key={idx}
                      className={`p-3.5 rounded-2xl border transition flex items-center gap-3 ${
                        parada.tipo_parada === 'coleta'
                          ? 'bg-amber-950/20 border-amber-800/60'
                          : 'bg-emerald-950/20 border-emerald-800/60'
                      }`}
                    >
                      {/* Número da Ordem da Sequência */}
                      <div className="flex flex-col items-center justify-center w-8 h-8 rounded-xl bg-slate-900 text-white font-black text-xs shrink-0 border border-slate-700">
                        #{idx + 1}
                      </div>

                      {/* Conteúdo da Parada */}
                      <div className="flex-1 min-w-0 space-y-1">
                        <div className="flex items-center gap-2">
                          <span
                            className={`px-2 py-0.5 rounded-md text-[10px] font-black uppercase tracking-wider ${
                              parada.tipo_parada === 'coleta'
                                ? 'bg-amber-500 text-slate-950'
                                : 'bg-emerald-500 text-slate-950'
                            }`}
                          >
                            {parada.tipo_parada === 'coleta' ? '1. Coleta / Retirada' : '2. Entrega / Descarga'}
                          </span>

                          <span className="text-[11px] font-bold text-white">OS #{parada.ordem_de_servico_id}</span>

                          {/* Seletor de Prioridade */}
                          <select
                            value={parada.prioridade}
                            onChange={(e) => handleMudarPrioridade(idx, e.target.value as PrioridadeRoteiro)}
                            className={`ml-auto text-[10px] font-bold px-2 py-0.5 rounded-lg border outline-none ${
                              parada.prioridade === 'urgente'
                                ? 'bg-red-950 text-red-300 border-red-700'
                                : parada.prioridade === 'alta'
                                ? 'bg-amber-950 text-amber-300 border-amber-700'
                                : 'bg-slate-900 text-slate-400 border-slate-800'
                            }`}
                          >
                            <option value="normal">Prioridade Normal</option>
                            <option value="alta">Prioridade Alta ⚡</option>
                            <option value="urgente">Urgente 🚨</option>
                            <option value="baixa">Baixa</option>
                          </select>
                        </div>

                        <p className="text-xs text-slate-200 font-medium truncate">
                          📍 {parada.endereco}
                        </p>
                      </div>

                      {/* Controles de Subir / Descer e Remover */}
                      <div className="flex items-center gap-1 shrink-0">
                        <button
                          onClick={() => handleMoverCima(idx)}
                          disabled={idx === 0}
                          className="p-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-slate-300 disabled:opacity-30 transition"
                          title="Subir posição"
                        >
                          <ArrowUp className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleMoverBaixo(idx)}
                          disabled={idx === paradas.length - 1}
                          className="p-1.5 rounded-lg bg-slate-900 hover:bg-slate-800 text-slate-300 disabled:opacity-30 transition"
                          title="Descer posição"
                        >
                          <ArrowDown className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleRemoverParada(idx)}
                          className="p-1.5 rounded-lg bg-red-950/60 hover:bg-red-900 text-red-400 transition"
                          title="Remover parada"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer com Botão de Salvar */}
        <div className="p-4 sm:p-6 border-t border-slate-800 bg-slate-950 flex items-center justify-between">
          <div className="text-xs text-slate-400">
            {paradas.length} {paradas.length === 1 ? 'parada sequenciada' : 'paradas sequenciadas no roteiro'}
          </div>

          <div className="flex items-center gap-3">
            <button
              onClick={onClose}
              className="px-4 py-2.5 rounded-xl border border-slate-700 hover:bg-slate-800 text-slate-300 text-xs font-bold transition"
            >
              Cancelar
            </button>
            <button
              onClick={handleSalvar}
              disabled={salvando || paradas.length === 0}
              className="px-5 py-2.5 rounded-xl bg-red-600 hover:bg-red-700 text-white text-xs font-bold transition flex items-center gap-2 disabled:opacity-50 shadow-lg shadow-red-600/30"
            >
              <Save className="w-4 h-4" />
              {salvando ? 'Salvando Roteiro...' : 'Salvar e Publicar Rota para o Motorista'}
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};
